import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';
import { DSButton, DSCard, DSPageHeader, DSTextarea, DSIcon } from '../design-system/primitives';
import StatusBadge from '../components/StatusBadge';
import './HostVolunteerRequests.css';

function HostVolunteerRequests({ embedded = false }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [reviewingRequest, setReviewingRequest] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();
  const currentUser = authService.getCurrentUser();
  const isAdmin = currentUser?.role === 'admin';

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await apiService.get(`/host-volunteer-requests${params}`);
      setRequests(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load volunteer requests');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    if (isAdmin) {
      fetchRequests();
    }
  }, [isAdmin, fetchRequests]);

  const handleApprove = async (requestId) => {
    setSubmitting(true);
    try {
      await apiService.post(`/host-volunteer-requests/${requestId}/approve`, {
        review_notes: reviewNotes
      });
      setReviewingRequest(null);
      setReviewNotes('');
      fetchRequests();
      setError(null);
    } catch (err) {
      setError('Failed to approve request');
      console.error('Error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeny = async (requestId) => {
    if (!reviewNotes.trim()) {
      setError('Please provide a reason for denial');
      return;
    }

    setSubmitting(true);
    try {
      await apiService.post(`/host-volunteer-requests/${requestId}/deny`, {
        review_notes: reviewNotes
      });
      setReviewingRequest(null);
      setReviewNotes('');
      fetchRequests();
      setError(null);
    } catch (err) {
      setError('Failed to deny request');
      console.error('Error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewEvent = (eventId) => {
    navigate(`/events/${eventId}`);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'denied':
        return 'error';
      default:
        return 'neutral';
    }
  };

  if (!isAdmin) {
    return (
      <div className="host-volunteer-requests">
        <div className="error">You must be an admin to access this page.</div>
      </div>
    );
  }

  return (
    <div className={`host-volunteer-requests${embedded ? ' embedded' : ''}`}>
      <DSPageHeader title={!embedded ? 'Host Volunteer Requests' : undefined} />

      {/* Four mutually exclusive states are chips, not a dropdown: they fit on one line,
          they show what is available without opening anything, and switching is one tap. */}
      <div className="request-filters" role="group" aria-label="Filter by status">
        {[
          { key: 'pending', label: 'Pending' },
          { key: 'approved', label: 'Approved' },
          { key: 'denied', label: 'Denied' },
          { key: 'all', label: 'All' }
        ].map((chip) => (
          <button
            key={chip.key}
            type="button"
            className={`request-filter${statusFilter === chip.key ? ' is-active' : ''}`}
            aria-pressed={statusFilter === chip.key}
            onClick={() => setStatusFilter(chip.key)}
          >
            {chip.label}
          </button>
        ))}
      </div>

      {error && <div className="error">{error}</div>}

      {loading ? (
        <div className="loading">Loading requests...</div>
      ) : (
        <>
          {requests.length === 0 ? (
            <DSCard cornerVariant="md" className="requests-empty">
              <p className="requests-empty__title">
                {statusFilter === 'pending' && 'No requests waiting on you'}
                {statusFilter === 'approved' && 'No approved requests'}
                {statusFilter === 'denied' && 'No denied requests'}
                {statusFilter === 'all' && 'No volunteer requests yet'}
              </p>
              <p className="requests-empty__detail">
                {statusFilter === 'pending'
                  ? 'Members who volunteer to host an event show up here for approval.'
                  : 'Members volunteer to host from the event page, and their requests land here.'}
              </p>
              {statusFilter !== 'all' && (
                <DSButton appearance="text" tone="secondary" size="sm" onClick={() => setStatusFilter('all')}>
                  Show all requests
                </DSButton>
              )}
            </DSCard>
          ) : (
            <div className="requests-list">
              {requests.map((request) => (
                <DSCard
                  key={request.id}
                  cornerVariant="md"
                  className="request-card"
                  title={`${request.first_name} ${request.last_name}`}
                  subtitle={<span className="user-email">{request.email}</span>}
                  actions={(
                    <StatusBadge variant={getStatusBadgeVariant(request.status)} uppercase>
                      {request.status}
                    </StatusBadge>
                  )}
                  footer={<small>Requested: {formatDate(request.created_at)}</small>}
                >

                  <div className="request-event">
                    <h4>{request.event_title}</h4>
                    <div className="event-details">
                      <span className="event-date"><DSIcon name="calendar" /> {formatDate(request.event_date)}</span>
                      {request.location && (
                        <span className="event-location"><DSIcon name="pin" /> {request.location}</span>
                      )}
                    </div>
                  </div>

                  {request.message && (
                    <div className="request-message">
                      <strong>Message from volunteer:</strong>
                      <p>{request.message}</p>
                    </div>
                  )}

                  {request.host_profile && (
                    <div className="host-profile">
                      <strong>Host Profile:</strong>
                      <p>{request.host_profile}</p>
                    </div>
                  )}

                  {request.status !== 'pending' && (
                    <div className="review-info">
                      <strong>Reviewed by:</strong> {request.reviewer_name || 'Unknown'}
                      <br />
                      <strong>Reviewed at:</strong> {formatDate(request.reviewed_at)}
                      {request.review_notes && (
                        <>
                          <br />
                          <strong>Notes:</strong> {request.review_notes}
                        </>
                      )}
                    </div>
                  )}

                  <div className="request-actions">
                    <DSButton
                      appearance="outline"
                      tone="secondary"
                      size="sm"
                      onClick={() => handleViewEvent(request.event_id)}
                    >
                      View Event
                    </DSButton>

                    {request.status === 'pending' && (
                      <>
                        {reviewingRequest === request.id ? (
                          <div className="review-form">
                            <DSTextarea
                              placeholder="Optional: Add notes or reason for decision..."
                              value={reviewNotes}
                              onChange={(e) => setReviewNotes(e.target.value)}
                              rows="2"
                            />
                            <div className="review-actions">
                              <DSButton
                                appearance="solid"
                                tone="primary"
                                size="sm"
                                onClick={() => handleApprove(request.id)}
                                disabled={submitting}
                              >
                                {submitting ? 'Processing...' : 'Approve'}
                              </DSButton>
                              <DSButton
                                appearance="solid"
                                tone="danger"
                                size="sm"
                                onClick={() => handleDeny(request.id)}
                                disabled={submitting}
                              >
                                Deny
                              </DSButton>
                              <DSButton
                                appearance="outline"
                                tone="neutral"
                                size="sm"
                                onClick={() => {
                                  setReviewingRequest(null);
                                  setReviewNotes('');
                                }}
                                disabled={submitting}
                              >
                                Cancel
                              </DSButton>
                            </div>
                          </div>
                        ) : (
                          <DSButton
                            appearance="solid"
                            tone="primary"
                            size="sm"
                            onClick={() => setReviewingRequest(request.id)}
                          >
                            Review
                          </DSButton>
                        )}
                      </>
                    )}
                  </div>
                </DSCard>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default HostVolunteerRequests;
